import {inject, Injectable} from '@angular/core';
import { APIService } from './api.service';
import { IReceiptData } from '../interfaces/IReceiptData';
import { Observable } from 'rxjs';
import { Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { AppConfigService } from './app-config.service';

(pdfMake as any).vfs = pdfFonts;

@Injectable({
  providedIn: 'root'
})
export class ReceiptService {

  private apiService: APIService = inject(APIService);
  private appConfigService: AppConfigService = inject(AppConfigService);

  public getReceiptByPaymentId(playerPaymentIdP: string, gameIdP: string): Observable<IReceiptData> {
    return this.apiService.MakeGetRequest<IReceiptData>(`payments/receipt/${playerPaymentIdP}/game/${gameIdP}`);
  }

  public async generateReceiptPDF(receiptDataP: IReceiptData): Promise<void> {
    const contentP: Content[] = [];
    const primaryColor = receiptDataP.PrimaryGameColor || '#4F46E5';
    const cdnRoot = this.appConfigService.cdnRoot();
    const logoUrl = `${cdnRoot}/assets/shared/causable-logo-white.svg`;

    // Fetch SVG as text
    const logoSvg = await this.fetchSvgAsText(logoUrl);

    // Header with colored banner
    contentP.push({
      canvas: [
        {
          type: 'rect',
          x: 0,
          y: 0,
          w: 515,
          h: 110,
          color: primaryColor
        }
      ]
    });

    // Logo at top of header
    contentP.push({
      svg: logoSvg,
      width: 100,
      alignment: 'center',
      margin: [0, -100, 0, 5]
    });

    contentP.push({
      text: 'PAYMENT RECEIPT',
      fontSize: 24,
      bold: true,
      color: 'white',
      alignment: 'center',
      margin: [0, 0, 0, 15]
    });

    contentP.push({
      text: receiptDataP.GameName,
      fontSize: 14,
      color: 'white',
      alignment: 'center',
      margin: [0, 0, 0, 15]
    });

    // Receipt summary box
    contentP.push({
      table: {
        widths: ['*'],
        body: [
          [
            {
              stack: [
                {
                  text: 'TOTAL PAID',
                  fontSize: 11,
                  color: '#6B7280',
                  alignment: 'center',
                  margin: [0, 10, 0, 5]
                },
                {
                  text: this.formatCurrency(receiptDataP.TransactionTotal),
                  fontSize: 32,
                  bold: true,
                  color: primaryColor,
                  alignment: 'center',
                  margin: [0, 0, 0, 10]
                }
              ],
              border: [false, false, false, false]
            }
          ]
        ]
      },
      margin: [0, 0, 0, 15]
    });

    // Transaction Details
    contentP.push({
      text: 'Transaction Details',
      fontSize: 16,
      bold: true,
      color: '#111827',
      margin: [0, 5, 0, 8]
    });

    contentP.push({
      table: {
        widths: ['*', '*'],
        body: [
          [
            { text: 'Confirmation Number', fontSize: 10, color: '#6B7280', border: [false, false, false, true], borderColor: ['', '', '', '#E5E7EB'], margin: [0, 5, 0, 5] },
            { text: receiptDataP.ConfirmationNumber, fontSize: 10, alignment: 'right', border: [false, false, false, true], borderColor: ['', '', '', '#E5E7EB'], margin: [0, 5, 0, 5] }
          ],
          [
            { text: 'Transaction Date', fontSize: 10, color: '#6B7280', border: [false, false, false, true], borderColor: ['', '', '', '#E5E7EB'], margin: [0, 5, 0, 5] },
            { text: receiptDataP.TransactionDate, fontSize: 10, alignment: 'right', border: [false, false, false, true], borderColor: ['', '', '', '#E5E7EB'], margin: [0, 5, 0, 5] }
          ],
          [
            { text: 'Receipt ID', fontSize: 10, color: '#6B7280', border: [false, false, false, false], margin: [0, 5, 0, 5] },
            { text: receiptDataP.ReceiptId, fontSize: 9, alignment: 'right', border: [false, false, false, false], margin: [0, 5, 0, 5] }
          ]
        ]
      },
      margin: [0, 0, 0, 15]
    });

    // Player Information
    contentP.push({
      text: 'Player Information',
      fontSize: 16,
      bold: true,
      color: '#111827',
      margin: [0, 5, 0, 8]
    });

    contentP.push({
      table: {
        widths: ['*', '*'],
        body: [
          [
            { text: 'Name', fontSize: 10, color: '#6B7280', border: [false, false, false, false], margin: [0, 5, 0, 5] },
            { text: `${receiptDataP.PlayerFirstName} ${receiptDataP.PlayerLastName}`, fontSize: 10, alignment: 'right', border: [false, false, false, false], margin: [0, 5, 0, 5] }
          ]
        ]
      },
      margin: [0, 0, 0, 15]
    });

    // Game Information
    contentP.push({
      text: 'Game Information',
      fontSize: 16,
      bold: true,
      color: '#111827',
      margin: [0, 5, 0, 8]
    });

    contentP.push({
      table: {
        widths: ['*', '*'],
        body: [
          [
            { text: 'Game', fontSize: 10, color: '#6B7280', border: [false, false, false, true], borderColor: ['', '', '', '#E5E7EB'], margin: [0, 5, 0, 5] },
            { text: receiptDataP.GameName, fontSize: 10, alignment: 'right', border: [false, false, false, true], borderColor: ['', '', '', '#E5E7EB'], margin: [0, 5, 0, 5] }
          ],
          [
            { text: 'Draw Date', fontSize: 10, color: '#6B7280', border: [false, false, false, true], borderColor: ['', '', '', '#E5E7EB'], margin: [0, 5, 0, 5] },
            { text: receiptDataP.GameInstanceDrawDate, fontSize: 10, alignment: 'right', border: [false, false, false, true], borderColor: ['', '', '', '#E5E7EB'], margin: [0, 5, 0, 5] }
          ],
          [
            { text: 'License Number', fontSize: 10, color: '#6B7280', border: [false, false, false, true], borderColor: ['', '', '', '#E5E7EB'], margin: [0, 5, 0, 5] },
            { text: receiptDataP.LicenseNumber, fontSize: 10, alignment: 'right', border: [false, false, false, true], borderColor: ['', '', '', '#E5E7EB'], margin: [0, 5, 0, 5] }
          ],
          [
            { text: 'Ticket Numbers', fontSize: 10, color: '#6B7280', border: [false, false, false, true], borderColor: ['', '', '', '#E5E7EB'], margin: [0, 5, 0, 5] },
            { text: receiptDataP.TicketNumbers.join(', '), fontSize: 10, alignment: 'right', border: [false, false, false, true], borderColor: ['', '', '', '#E5E7EB'], margin: [0, 5, 0, 5] }
          ],
          [
            { text: 'Autoplay', fontSize: 10, color: '#6B7280', border: [false, false, false, false], margin: [0, 5, 0, 5] },
            { text: receiptDataP.AutoPlayEnabled ? 'Enabled' : 'Disabled', fontSize: 10, alignment: 'right', border: [false, false, false, false], margin: [0, 5, 0, 5] }
          ]
        ]
      },
      margin: [0, 0, 0, 20]
    });

    // Footer
    contentP.push({
      canvas: [
        {
          type: 'line',
          x1: 0,
          y1: 0,
          x2: 515,
          y2: 0,
          lineWidth: 1,
          lineColor: '#E5E7EB'
        }
      ],
      margin: [0, 15, 0, 10]
    });

    contentP.push({
      columns: [
        {
          svg: logoSvg,
          width: 80,
          alignment: 'left'
        },
        {
          stack: [
            {
              text: 'Thank you for your purchase!',
              fontSize: 12,
              alignment: 'center',
              color: '#6B7280'
            },
            {
              text: `Minimum participation age: ${receiptDataP.MinimumParticipationAge}+`,
              fontSize: 9,
              alignment: 'center',
              color: '#9CA3AF',
              margin: [0, 5, 0, 0]
            }
          ],
          width: '*'
        },
        {
          text: '',
          width: 80
        }
      ],
      margin: [0, 0, 0, 5]
    });

    const docDef: TDocumentDefinitions = {
      pageMargins: [40, 40, 40, 60],
      footer: (currentPage, pageCount) => {
        return {
          text: `Page ${currentPage} of ${pageCount}`,
          alignment: 'center',
          fontSize: 9,
          color: '#9CA3AF',
          margin: [0, 10, 0, 10]
        };
      },
      content: contentP
    };

    const pdf = pdfMake.createPdf(docDef);
    const filename = `receipt-${receiptDataP.ConfirmationNumber}.pdf`;
    pdf.download(filename);
  }

  private formatCurrency(amountP: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'CAD'
    }).format(amountP);
  }

  private async fetchSvgAsText(urlP: string): Promise<string> {
    const response = await fetch(urlP);
    return response.text();
  }
}
